import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Play, Pause, Rewind, FastForward, Download } from 'lucide-react';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const SPEEDS = [1, 1.25, 1.5, 2];

/**
 * Compliance-grade audio player for call recordings. Loads the recording as
 * an authed blob (so range-seeking works fully in-browser) and exposes a
 * scrubber plus skip controls so reviewers can jump back/forward and replay
 * any section.
 */
export default function RecordingPlayerDialog({ open, recordingSid, title, subtitle, onClose }) {
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (open && recordingSid) {
      setLoading(true); setCur(0); setDur(0); setPlaying(false); setError(false);
      api.post(`/voice/recordings/${recordingSid}/media-token`)
        .then((res) => {
          if (cancelled) return;
          const base = api.defaults.baseURL || '';
          const url = `${base}/voice/recordings/${recordingSid}/stream?token=${encodeURIComponent(res.data.token)}`;
          urlRef.current = url;
          if (audioRef.current) {
            audioRef.current.src = url;
            audioRef.current.load();
          }
        })
        .catch((e) => { if (!cancelled) { setError(true); toast.error(getErrorMessage(e, 'Could not load the recording')); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => {
      cancelled = true;
      urlRef.current = null;
      setPlaying(false);
    };
  }, [open, recordingSid, reloadKey]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  }, []);

  const skip = useCallback((secs) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.duration || 0), a.currentTime + secs));
    setCur(a.currentTime);
  }, []);

  const onScrub = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const t = Number(e.target.value);
    a.currentTime = t;
    setCur(t);
  };

  const changeSpeed = () => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const download = async () => {
    try {
      const res = await api.get(`/voice/recordings/${recordingSid}/stream`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `recording-${recordingSid}.mp3`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(getErrorMessage(e, 'Download failed')); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="recording-player-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{title || 'Call recording'}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">Play and review the call recording.</DialogDescription>
        {subtitle && <p className="text-xs text-slate-500 -mt-2">{subtitle}</p>}

        <audio
          ref={audioRef}
          onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
          onTimeUpdate={(e) => setCur(e.target.currentTime || 0)}
          onEnded={() => setPlaying(false)}
          preload="metadata"
          referrerPolicy="no-referrer"
          className="hidden"
        />

        {error ? (
          <div className="py-8 flex flex-col items-center justify-center text-slate-500 gap-3" data-testid="recording-error">
            <span className="text-sm">The recording could not be loaded.</span>
            <Button size="sm" variant="outline" onClick={() => setReloadKey((k) => k + 1)} data-testid="recording-retry">
              <Loader2 className="mr-1.5 hidden" size={14} /> Retry
            </Button>
          </div>
        ) : loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500 gap-2">
            <Loader2 className="animate-spin" size={16} /> Loading recording…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Scrubber */}
            <input
              type="range"
              min={0}
              max={dur || 0}
              step={0.1}
              value={cur}
              onChange={onScrub}
              className="w-full accent-[#0f2547] cursor-pointer"
              data-testid="recording-scrubber"
            />
            <div className="flex justify-between text-xs text-slate-500 font-mono">
              <span data-testid="recording-current-time">{fmt(cur)}</span>
              <span data-testid="recording-duration">{fmt(dur)}</span>
            </div>

            {/* Transport */}
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => skip(-60)} data-testid="recording-back-1m" title="Back 1 min">
                <Rewind size={14} className="mr-1" />1m
              </Button>
              <Button size="sm" variant="outline" onClick={() => skip(-15)} data-testid="recording-back-15s" title="Back 15s">
                <Rewind size={14} className="mr-1" />15s
              </Button>
              <Button size="icon" onClick={toggle} className="bg-[#0f2547] h-11 w-11 rounded-full" data-testid="recording-play-toggle">
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => skip(15)} data-testid="recording-fwd-15s" title="Forward 15s">
                15s<FastForward size={14} className="ml-1" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => skip(60)} data-testid="recording-fwd-1m" title="Forward 1 min">
                1m<FastForward size={14} className="ml-1" />
              </Button>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button size="sm" variant="ghost" onClick={changeSpeed} data-testid="recording-speed">
                Speed: {speed}x
              </Button>
              <Button size="sm" variant="ghost" onClick={download} data-testid="recording-download">
                <Download size={14} className="mr-1" /> Download
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
